import { t } from "@lingui/macro";
// apps/client/src/pages/admin/dashboard/audit/page.tsx

import { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
} from "@reactive-resume/ui";
import {
  CaretCircleLeftIcon,
  CaretCircleRightIcon,
  DownloadSimpleIcon,
  ReceiptIcon,
  XIcon,
} from "@phosphor-icons/react";
import { cn } from "@reactive-resume/utils";
import { toast } from "sonner";

import { Column, DataTable } from "../_components/datatables";
import {
  useAuditLogs,
  useExportAudit,
  type AuditFilters,
  type AuditLogEntry,
} from "@/client/services/audit/audit";

/**
 * ─────────────────────────────────────────────────────────────────────────────
 *  JOURNAL D'AUDIT (admin)
 * ─────────────────────────────────────────────────────────────────────────────
 *  Table filtrable (qui/quoi/ressource/quand/statut) + pagination serveur +
 *  panneau de détail (diff `changes`, metadata, user-agent) + export CSV.
 *  Répond à : QUI a fait QUOI, sur QUELLE ressource, QUAND, depuis OÙ.
 * ─────────────────────────────────────────────────────────────────────────────
 */

const STATUS_META: Record<AuditLogEntry["status"], { label: string; className: string }> = {
  success: {
    label: "Succès",
    className: "bg-emerald-500/10 text-emerald-600 ",
  },
  failure: { label: "Échec", className: "bg-red-500/10 text-red-600 " },
  warning: { label: "Alerte", className: "bg-amber-500/10 text-amber-600 " },
};

const fmtDateTime = (iso: string) =>
  new Intl.DateTimeFormat("fr-FR", { dateStyle: "short", timeStyle: "medium" }).format(
    new Date(iso),
  );

const shortId = (id?: string | null) => (id ? (id.length > 10 ? `${id.slice(0, 10)}…` : id) : "—");

/** `dateSchema` côté serveur est un ISO datetime strict → conversion UTC. */
const isoDayStart = (dateInput: string) => new Date(`${dateInput}T00:00:00Z`).toISOString();
const isoDayAfter = (dateInput: string) => {
  const d = new Date(`${dateInput}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString();
};

export const AuditAdminPage = () => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [searchField, setSearchField] = useState<"action" | "resource">("action");
  const [status, setStatus] = useState<"all" | AuditLogEntry["status"]>("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [detail, setDetail] = useState<AuditLogEntry | null>(null);

  const exportCsv = useExportAudit();

  // Debounce recherche 350 ms — reset de la page à chaque changement de filtre.
  useEffect(() => {
    const t = setTimeout(() => {
      setDebounced(search.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [search]);

  const filters: AuditFilters = useMemo(
    () => ({
      page,
      limit: 20,
      action: searchField === "action" && debounced ? debounced : undefined,
      resource: searchField === "resource" && debounced ? debounced : undefined,
      status: status === "all" ? undefined : status,
      startDate: startDate ? isoDayStart(startDate) : undefined,
      endDate: endDate ? isoDayAfter(endDate) : undefined,
    }),
    [page, debounced, searchField, status, startDate, endDate],
  );

  const { logs, pagination, loading } = useAuditLogs(filters);

  const hasFilters =
    Boolean(debounced) || status !== "all" || Boolean(startDate) || Boolean(endDate);

  const resetFilters = () => {
    setSearch("");
    setStatus("all");
    setStartDate("");
    setEndDate("");
    setPage(1);
  };

  const handleExport = async () => {
    try {
      await exportCsv({ ...filters, page: undefined, limit: undefined });
      toast.success(t`Export CSV (.csv) téléchargé`);
    } catch {
      toast.error(t`Échec de l'export CSV (.csv)`);
    }
  };

  const columns: Column<AuditLogEntry>[] = [
    {
      key: "createdAt",
      header: "Horodatage",
      render: (e) => <span className="whitespace-nowrap text-sm">{fmtDateTime(e.createdAt)}</span>,
    },
    {
      key: "userId",
      header: "Utilisateur",
      render: (e) =>
        e.user ? (
          <div>
            <p className="font-medium text-admin-text-primary">{e.user.name}</p>
            <p className="text-xs text-admin-text-muted">{e.user.email}</p>
          </div>
        ) : (
          <span className="text-xs italic text-admin-text-muted">Système</span>
        ),
    },
    {
      key: "action",
      header: "Action",
      render: (e) => (
        <span className="rounded bg-admin-surface-elevated px-2 py-0.5 font-mono text-xs text-admin-text-secondary">
          {e.action}
        </span>
      ),
    },
    {
      key: "resource",
      header: "Ressource",
      render: (e) => (
        <span className="text-sm">
          {e.resource}
          <span className="ml-1.5 font-mono text-xs text-admin-text-muted">
            #{shortId(e.resourceId)}
          </span>
        </span>
      ),
    },
    {
      key: "status",
      header: "Statut",
      render: (e) => (
        <Badge className={cn("border-0", STATUS_META[e.status].className)}>
          {STATUS_META[e.status].label}
        </Badge>
      ),
    },
    {
      key: "ipAddress",
      header: "IP",
      align: "right",
      render: (e) => (
        <span className="font-mono text-xs text-admin-text-muted">{e.ipAddress || "—"}</span>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      {/* En-tête */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-admin-text-primary">Journal d'audit</h1>
          <p className="text-sm text-admin-text-muted">
            Traçabilité complète des actions administratives — qui, quoi, quand.
          </p>
        </div>
        <Button variant="outline" onClick={handleExport} className="gap-2">
          <DownloadSimpleIcon size={16} />
          {t`Exporter en CSV (.csv)`}
        </Button>
      </div>

      {/* Filtres */}
      <div className="flex flex-wrap items-center gap-3">
        <Input
          placeholder={
            searchField === "action"
              ? "Filtrer par action (ex. WITHDRAWAL)…"
              : "Filtrer par ressource (ex. withdrawal)…"
          }
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full max-w-xs border-admin-border bg-admin-surface"
        />
        <Select
          value={searchField}
          onValueChange={(v) => {
            setSearchField(v as typeof searchField);
            setPage(1);
          }}
        >
          <SelectTrigger className="w-40 border-admin-border bg-admin-surface">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="action">Champ : Action</SelectItem>
            <SelectItem value="resource">Champ : Ressource</SelectItem>
          </SelectContent>
        </Select>
        <Select
          value={status}
          onValueChange={(v) => {
            setStatus(v as typeof status);
            setPage(1);
          }}
        >
          <SelectTrigger className="w-36 border-admin-border bg-admin-surface">
            <SelectValue placeholder="Statut" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous statuts</SelectItem>
            <SelectItem value="success">Succès</SelectItem>
            <SelectItem value="failure">Échecs</SelectItem>
            <SelectItem value="warning">Alertes</SelectItem>
          </SelectContent>
        </Select>
        <Input
          type="date"
          value={startDate}
          onChange={(e) => {
            setStartDate(e.target.value);
            setPage(1);
          }}
          className="w-40 border-admin-border bg-admin-surface"
          title="Date de début"
        />
        <Input
          type="date"
          value={endDate}
          onChange={(e) => {
            setEndDate(e.target.value);
            setPage(1);
          }}
          className="w-40 border-admin-border bg-admin-surface"
          title="Date de fin"
        />
        {hasFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={resetFilters}
            className="gap-1.5 text-admin-text-muted"
          >
            <XIcon size={14} /> Réinitialiser
          </Button>
        )}
      </div>

      {/* Table + pagination serveur */}
      <div className="flex flex-col gap-3">
        <DataTable
          columns={columns}
          data={logs}
          loading={loading}
          onRowClick={(e) => setDetail(e)}
          pageSize={20}
          emptyState={
            <div className="flex flex-col items-center gap-2">
              <ReceiptIcon size={32} className="text-admin-text-muted" />
              <p>Aucune entrée d'audit pour ces filtres.</p>
            </div>
          }
        />

        {pagination && pagination.pages > 0 && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-admin-text-muted">
              {pagination.total.toLocaleString("fr-FR")} entrée(s) — page {pagination.page} sur{" "}
              {pagination.pages}
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                <CaretCircleLeftIcon size={16} />
              </Button>
              <Button
                variant="outline"
                size="icon"
                disabled={page >= pagination.pages}
                onClick={() => setPage((p) => Math.min(pagination!.pages, p + 1))}
              >
                <CaretCircleRightIcon size={16} />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Détail */}
      {detail && <AuditDetailDialog entry={detail} onClose={() => setDetail(null)} />}
    </div>
  );
};

/** ── Panneau détail : diff changes, metadata, user-agent ─────────────────── */

const AuditDetailDialog = ({ entry, onClose }: { entry: AuditLogEntry; onClose: () => void }) => {
  const changes = entry.changes && Object.keys(entry.changes).length > 0 ? entry.changes : null;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto border-admin-border bg-admin-surface">
        <DialogHeader>
          <DialogTitle className="font-mono text-base text-admin-text-primary">
            {entry.action}
          </DialogTitle>
          <DialogDescription className="flex flex-wrap items-center gap-2 pt-1">
            <Badge className={cn("border-0", STATUS_META[entry.status].className)}>
              {STATUS_META[entry.status].label}
            </Badge>
            <span className="text-admin-text-muted">
              {fmtDateTime(entry.createdAt)} · IP {entry.ipAddress || "inconnue"}
            </span>
          </DialogDescription>
        </DialogHeader>

        {/* Acteur */}
        <div className="rounded-xl-admin border border-admin-border bg-admin-surface-elevated p-3 text-sm">
          <p className="text-admin-text-secondary">
            <span className="text-admin-text-muted">Acteur :</span>{" "}
            {entry.user ? `${entry.user.name} (${entry.user.email})` : "Système"}
          </p>
          <p className="text-admin-text-secondary">
            <span className="text-admin-text-muted">Ressource :</span> {entry.resource}
            <span className="ml-1.5 font-mono text-xs text-admin-text-muted">
              #{entry.resourceId ?? "—"}
            </span>
          </p>
        </div>

        {/* Diff des changements */}
        {changes && (
          <div className="flex flex-col gap-1.5">
            <p className="text-sm font-medium text-admin-text-secondary">Changements</p>
            <pre className="max-h-64 overflow-auto rounded-lg border border-admin-border bg-admin-surface-elevated p-3 font-mono text-xs leading-relaxed text-admin-text-secondary">
              {JSON.stringify(changes, null, 2)}
            </pre>
          </div>
        )}

        {/* Metadata */}
        {entry.metadata && Object.keys(entry.metadata).length > 0 && (
          <div className="flex flex-col gap-1.5">
            <p className="text-sm font-medium text-admin-text-secondary">Métadonnées</p>
            <pre className="max-h-40 overflow-auto rounded-lg border border-admin-border bg-admin-surface-elevated p-3 font-mono text-xs leading-relaxed text-admin-text-secondary">
              {JSON.stringify(entry.metadata, null, 2)}
            </pre>
          </div>
        )}

        {/* User agent */}
        {entry.userAgent && (
          <div className="flex flex-col gap-1.5">
            <p className="text-sm font-medium text-admin-text-secondary">Navigateur</p>
            <p className="break-all rounded-lg border border-admin-border bg-admin-surface-elevated p-2 font-mono text-xs text-admin-text-muted">
              {entry.userAgent}
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
